import React, {useEffect, useMemo, useState} from "react";

import SGrid from "../../components/grid/grid.component";
import {fetch} from "../../shared/api/v1";
import {useAuth} from "../../providers/auth/auth-provider";
import {useMessage} from "../../providers/message/message-provider";
import type {components} from "../../shared/api/v1/openapi.types";

type CommentEntity = components["schemas"]["ConnectionComment"];

type ConnectionCommentsProps = {
  connectionId?: string;
};

const resolveTotal = (page: number, pageSize: number, count: number) => {
  if (page < 1 || pageSize < 1) {
    return count;
  }
  if (count < pageSize) {
    return (page - 1) * pageSize + count;
  }
  return page * pageSize + 1;
};

const extractErrorMessage = (error: unknown) => {
  if (!error || typeof error !== "object") {
    return undefined;
  }
  const maybeMessage = (error as { message?: string }).message;
  if (maybeMessage) {
    return maybeMessage;
  }
  const nestedMessage = (error as { error?: { message?: string } }).error?.message;
  if (nestedMessage) {
    return nestedMessage;
  }
  return (error as { data?: { message?: string } }).data?.message;
};

export const ConnectionComments = ({connectionId}: ConnectionCommentsProps) => {
  const {token} = useAuth();
  const {showError} = useMessage();
  const [comments, setComments] = useState<CommentEntity[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  const canLoad = !!token && !!connectionId && connectionId !== "new";

  useEffect(() => {
    let isMounted = true;
    const loadComments = async () => {
      if (!canLoad) {
        setComments([]);
        setTotal(0);
        return;
      }
      setIsLoading(true);
      const response = await fetch.GET("/connections/{id}/comments", {
        params: {
          path: {
            id: Number(connectionId),
          },
          query: {
            page,
            limit: pageSize,
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!isMounted) {
        return;
      }
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        showError({
          message: message ?? "Ошибка загрузки сообщений",
        });
        setComments([]);
        setTotal(0);
        setIsLoading(false);
        return;
      }
      const payload = response.data as
        | CommentEntity[]
        | { comments?: CommentEntity[]; total?: number }
        | undefined;
      const data = Array.isArray(payload)
        ? payload
        : payload && Array.isArray(payload.comments)
          ? payload.comments ?? []
          : [];
      setComments(data);
      setTotal(
        !Array.isArray(payload) && typeof payload?.total === "number"
          ? payload.total
          : resolveTotal(page, pageSize, data.length)
      );
      setIsLoading(false);
    };
    loadComments();
    return () => {
      isMounted = false;
    };
  }, [canLoad, connectionId, page, pageSize, showError, token]);

  const columns = useMemo(
    () => [
      {
        field: "message",
        header: "Сообщение",
        body: (item: CommentEntity) => <span>{item.message ?? "-"}</span>,
      },
      {
        field: "createdBy",
        header: "Автор",
        body: (item: CommentEntity) => <span>{item.createdBy?.name ?? item.createdBy?.login ?? "-"}</span>,
      },
      {
        headerStyle: {width: "170px"},
        field: "createdAt",
        header: "Получено",
        body: (item: CommentEntity) => (
          <span>
            {item.createdAt
              ? new Date(item.createdAt).toLocaleString("ru-RU", {
                dateStyle: "short",
                timeStyle: "short",
              })
              : "-"}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <SGrid
      header={<span>Сообщения от ТСО</span>}
      value={comments}
      loading={isLoading}
      paginator
      rows={pageSize}
      totalRecords={total}
      first={(page - 1) * pageSize}
      onPage={(e) => {
        const nextRows = e.rows ?? pageSize;
        const nextPage = Math.floor((e.first ?? 0) / nextRows) + 1;
        setPageSize(nextRows);
        setPage(nextPage);
      }}
      emptyMessage="Сообщения отсутствуют"
      columns={columns}
    />
  );
};
